import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Button from 'tbk-components/src/components/Button';
import { Generic } from 'react-structured-data';
import LazyJSONLD from '../LazyJSONLD';

export interface IAccordionItems {
	/**
	 * Title
	 * @control richtext
	 */
	title?: string;
	itemId?: string;
	expanded?: boolean;
	/**
	 * Item Text
	 * @control richtext
	 */
	content?: string;
	/**
	 * Item Image
	 */
	image?: IImage;
}
export interface IAccordionProps extends IBaseElementProps {
	/**
	 * @control uniqueid
	 * @prefix calian-accordion
	 */
	id?: string;
	/**
	 * Accordion Heading
	 */
	heading?: string;
	/**
	 * Is this accordion going to be used as a FAQ list?
	 */
	isFAQ?: boolean;
	items?: IAccordionItems[];
	/**
	 * This will create the accordions in three separate columns only on desktop
	 */
	threeColumned?: boolean;
}

/**
 * Accordion
 * @block
 * @icon list-view
 */
const Accordion: React.FC<IAccordionProps> = ({
	id,
	heading,
	items = [],
	isFAQ = false,
	threeColumned = false,
}) => {
	const accordionID = id; // is uniqueid

	if (!items) {
		return null;
	}
	let faqs = null;

	// check if the page has FAQ in the heading
	if (isFAQ) {
		faqs = items.map((item, index) => {
			return {
				'@type': 'Question',
				name: item.title,
				acceptedAnswer: {
					'@type': 'Answer',
					text: item.content,
				},
			};
		});
	}

	return (
		<div
			id={accordionID}
			className={cn(
				'calian-accordion',
				'accordion-flush',
				threeColumned && 'three-columned',
			)}
		>
			{faqs && (
				<LazyJSONLD>
					<Generic
						type="page"
						jsonldtype="FAQPage"
						schema={{ mainEntity: faqs }}
					/>
				</LazyJSONLD>
			)}
			{heading && (
				<h3 className="calian-accordion__heading">{heading}</h3>
			)}
			{items.length > 0 && (
				<ul className="calian-accordion__items">
					{items.map((item, index) => (
						<li className="calian-accordion__item" key={index}>
							<div
								className="calian-accordion__header"
								id={`${accordionID}--header-${index}`}
							>
								<Button
									className={cn(
										'calian-accordion__button',
										!item.expanded && 'collapsed',
									)}
									data-bs-toggle="collapse"
									data-bs-target={`#${accordionID}--collapse-${index}`}
									aria-expanded={item.expanded}
									aria-controls={`${accordionID}--collapse-${index}`}
								>
									<div
										className="calian-accordion__title"
										dangerouslySetInnerHTML={{
											__html: item.title,
										}}
									/>

									<div className="calian-accordion__icon">
										<span className="icon icon-caret-left-slim"></span>
									</div>
								</Button>
							</div>
							<div
								className={cn(
									'calian-accordion__collapse',
									'collapse',
									item.expanded ? 'show' : '',
								)}
								id={`${accordionID}--collapse-${index}`}
								aria-labelledby={`calian-accordion-header-${index}`}
								data-bs-parent={`#${accordionID}`}
							>
								<div className="calian-accordion__body-container">
									<div
										className="calian-accordion__body"
										dangerouslySetInnerHTML={{
											__html: item.content,
										}}
									/>
									{item.image?.src && (
										<div className="calian-accordion__image-container">
											<div className="calian-accordion__image-wrapper">
												<Image
													{...item.image}
													className="calian-accordion__image img-fluid"
													alt={item.title}
												/>
											</div>
										</div>
									)}
								</div>
							</div>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default Accordion;
